import React, { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles'; import {
  Grid,
  OutlinedInput,
  FormControl,
  IconButton,
  Button,
  CircularProgress
} from '@material-ui/core';
import { faPaperclip, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { useWindowSize } from '../../customHooks/useWindowSize';
import translate from '../../i18next/funcTranslateHelper';

const SearchBar = ({
  hashType,
  hashPlaceholder,
  uploadingFile,
  setUploadingFile,
  origin
}) => {
  const useStyles = makeStyles(theme => ({
    mainFormContainer: {
      width: '100%',
      display: 'flex',
      flexWrap: 'wrap',
      minWidth: 0
    },
    searchBarItemResult: {
      padding: 0,
      margin: 0,
      [theme.breakpoints.down('sm')]: {
        maxWidth: 749,
        flexGrow: 1
      },
      [theme.breakpoints.up('sm')]: {
        maxWidth: 749,
        paddingRight: 8,
        flexGrow: 1
      }
    },
    searchBarItemSearcher: {
      padding: 0,
      margin: 0,
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        minWidth: origin === 'searcher' ? 164 : 0
      },
      [theme.breakpoints.up('sm')]: {
        maxWidth: origin === 'searcher' ? 619 : 749
      }
    }
  }));
  const useButtonBigStyles = makeStyles(theme => ({
    root: {
      opacity: '0.9',
      background: '#FFFFFF',
      boxShadow: '0 8px 16px 0 #2A326F',
      borderRadius: 2,
      width: 160,
      height: 62,
      maxWidth: 160,
      marginBottom: 23,
      backgroundImage: 'linear-gradient(226deg, #F96500 0% , #CA005A 100%)'
    },
    label: {
      color: 'white',
      fontFamily: 'Roboto-Regular',
      fontSize: '18px',
      letterSpacing: 0
    }
  }));

  const useButtonSmallStyles = makeStyles(theme => ({
    root: {
      borderRadius: 2,
      width: 48,
      height: 48,
      minWidth: 48,
      padding: 0,
      marginBottom: 23,
      backgroundImage: 'linear-gradient(226deg, #F96500 0% , #CA005A 100%)'
    },
    label: {
      color: 'white',
      width: 48
    },
    endIcon: {
      margin: 0
    }
  }));

  const useOutlinedInputStyles = makeStyles(theme => ({
    root: {
      opacity: '0.9',
      background: '#FFFFFF',
      boxShadow:
        origin === 'searcher'
          ? '0 8px 16px 0 #2A326F'
          : '0 8px 16px 0 rgba(42,50,111,0.14)',
      borderRadius: 2,
      width: '100%',
      padding: 0,
      [theme.breakpoints.up('sm')]: {
        height: 62
      },
      [theme.breakpoints.down('xs')]: {
        height: origin === 'result' ? 48 : 62,
        minWidth: 200
      },
      marginBottom: 23
    }
  }));

  const useFormInputStyles = makeStyles(theme => ({
    root: {
      flexGrow: 1,
      marginTop: origin === 'searcher' ? 27 : 0,
      width: '100%'
    }
  }));

  const classes = useStyles();
  const bigButtonStyles = useButtonBigStyles();
  const smallButtonStyles = useButtonSmallStyles();
  const outlinedStyles = useOutlinedInputStyles();
  const formInputStyles = useFormInputStyles();
  const size = useWindowSize();

  const [key, setKey] = useState('');

  const inputRef = useRef(null);

  const onChangeFile = async event => {
    event.stopPropagation();
    event.preventDefault();
    setUploadingFile(true);
    const file = await readFileAsync(event.target.files[0]);
    const SHA256 = CryptoJS.algo.SHA256.create();
    const wordBuffer = CryptoJS.lib.WordArray.create(file);
    SHA256.update(wordBuffer);
    const encrypted = SHA256.finalize().toString();
    setKey(encrypted);
    setUploadingFile(false);
  };

  const readFileAsync = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const icon = uploading => {
    return (
      <IconButton
        className='MyCustomButton'
        onClick={() => {
          inputRef.current
            ? inputRef.current.click()
            : document.getElementById('input-file').click();
        }}
      >
        {uploading ? (
          <CircularProgress size={22} />
        ) : (
          <FontAwesomeIcon icon={faPaperclip} />
        )}
      </IconButton>
    );
  };

  return (
    <FormControl classes={formInputStyles} noValidate autoComplete='off'>
      <Grid
        container
        direction={
          origin === 'searcher' ? (size.width >= 890 ? 'row' : 'column') : 'row'
        }
        justify={origin === 'searcher' ? 'space-between' : 'space-between'}
        alignItems='center'
        className={classes.mainFormContainer}
      >
        <Grid
          item
          className={
            origin === 'searcher'
              ? classes.searchBarItemSearcher
              : classes.searchBarItemResult
          }
        >
          <OutlinedInput
            id='outlined-name'
            classes={outlinedStyles}
            value={key}
            onChange={e => {
              setKey(e.target.value);
            }}
            variant='outlined'
            placeholder={hashPlaceholder}
            endAdornment={(hashType !== 'transaction') && icon(uploadingFile)}
          />
          <input
            id='input-file'
            type='file'
            ref={inputRef}
            style={{ height: '100%', display: 'none' }}
            onChange={onChangeFile.bind(this)}
          />
        </Grid>
        <Grid item>
          <Link
            to={`/search/${hashType}/${key}`}
            style={{ textDecoration: 'none', minWidth: 159 }}
          >
            <Button
              size='large'
              classes={
                origin === 'result' && size.width < 600
                  ? smallButtonStyles
                  : bigButtonStyles
              }
              endIcon={
                <FontAwesomeIcon
                  icon={faSearch}
                  style={{
                    marginLeft:
                      origin === 'result' && size.width < 600 ? 0 : 17.9,
                    padding: 0
                  }}
                />
              }
            >
              {!(origin === 'result' && size.width < 600) && translate('Trazer:searchButton')}
            </Button>
          </Link>
        </Grid>
      </Grid>
    </FormControl>
  );
};

export default SearchBar;
